import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import SettingsHeader from './SettingsHeader';
import { LoadingSpinner } from '@/components';
import { useFeatureGate } from '@statsig/react-bindings';
import { useUserContext } from '@/contexts/UserContext';
import { useIsAdmin } from '@/hooks/useAuth';
import { useUserManagementEnabled } from '@/hooks/useUserManagementEnabled';
import clsx from 'clsx';
import UserManagement from './UserManagement/UserManagement';
import AccessPage from './UserManagement/AccessPage';
import MyProfile from './UserManagement/MyProfile';

const TagManagement = lazy(() => import('./TagManagement'));
const TokenManagement = lazy(() => import('./TokenManagement'));
const DeviceManagementPage = lazy(() => import('../DeviceManagementPage'));

/**
 * Admin guard: renders children for admins, redirects non-admins to /settings/access
 */
const AdminGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return <Navigate to="/settings/access" replace />;
  }
  return <>{children}</>;
};

/**
 * Member guard: renders children for non-admins, redirects admins to /settings/users
 */
const MemberGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAdmin = useIsAdmin();
  if (isAdmin) {
    return <Navigate to="/settings/users" replace />;
  }
  return <>{children}</>;
};

export const SettingsPage = () => {
  const { value: enableApiTokens } = useFeatureGate('api_tokens');
  const userManagementEnabled = useUserManagementEnabled();
  const { sidebarCollapsed } = useUserContext();
  return (
    <div className="bg-surface-sunken w-full">
      {/* we can make the input text dynamic when there are other funcitons to display on the settings page, beyond just tags */}
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {enableApiTokens && (
            <Route
              path="/settings/tokens"
              element={
                <>
                  <SettingsHeader headerText="Token Management" subText="Manage your API tokens" />
                  <TokenManagement />
                </>
              }
            />
          )}
          {userManagementEnabled && (
            <Route
              path="/settings/profile"
              element={
                <>
                  <SettingsHeader
                    headerText="My Profile"
                    subText="View and manage your personal account information."
                  />
                  <MyProfile />
                </>
              }
            />
          )}
          <Route
            path="/settings/tags"
            element={
              <>
                <SettingsHeader
                  headerText="Tags Management"
                  subText="Manage your preferences and settings"
                />
                <TagManagement />
              </>
            }
          />
          {userManagementEnabled && (
            <Route
              path="/settings/users"
              element={
                <AdminGuard>
                  <SettingsHeader
                    headerText="User Management"
                    subText="Manage user access by assigning roles and spaces across your organization."
                  />
                  <UserManagement />
                </AdminGuard>
              }
            />
          )}
          {userManagementEnabled && (
            <Route
              path="/settings/access"
              element={
                <MemberGuard>
                  <SettingsHeader
                    headerText="Access"
                    subText="View all spaces you're assigned to and your access level for each."
                  />
                  <AccessPage />
                </MemberGuard>
              }
            />
          )}
          <Route
            path="/settings/devices"
            element={
              <>
                <SettingsHeader
                  headerText="Devices Management"
                  subText="Monitor and manage all sensors and hives deployed across your spaces."
                />
                <div
                  className={clsx(
                    'transition-all duration-300',
                    !sidebarCollapsed ? 'w-[calc(100vw-260px)]' : 'w-[calc(100vw-56px)]',
                  )}
                >
                  <DeviceManagementPage />
                </div>
              </>
            }
          />
          <Route path="*" element={<Navigate to="/settings/tags" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default SettingsPage;
